import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { theme, injectKeyframes, useTranslationTyped } from '@kantera/ui';
import { KANTERA_EMAIL } from '@kantera/types';

interface HelpLink {
  labelKey: string;
  href?: string;
  action?: () => void;
}

interface PageHelpConfig {
  links: HelpLink[];
}

const PAGE_HELP: Record<string, PageHelpConfig> = {
  '/': {
    links: [
      { labelKey: 'webHelpButton.linkCreateTeam', href: '/teams/new' },
      { labelKey: 'webHelpButton.linkScheduleMatch', href: '/matches/new' },
    ],
  },
  '/players': {
    links: [
      { labelKey: 'webHelpButton.linkAddPlayer', href: '/players/new' },
      { labelKey: 'webHelpButton.linkReviewCoppa', href: '/compliance' },
    ],
  },
  '/players/new': {
    links: [
      { labelKey: 'webHelpButton.linkCoppaDashboard', href: '/compliance' },
      { labelKey: 'webHelpButton.linkManageConsent', href: '/consent' },
    ],
  },
  '/matches': {
    links: [
      { labelKey: 'webHelpButton.linkCreateMatch', href: '/matches/new' },
      { labelKey: 'webHelpButton.linkViewReports', href: '/reports' },
    ],
  },
  '/teams': {
    links: [
      { labelKey: 'webHelpButton.linkCreateTeam', href: '/teams/new' },
      { labelKey: 'webHelpButton.linkAddPlayer', href: '/players' },
    ],
  },
  '/settings': {
    links: [
      { labelKey: 'webHelpButton.linkPrivacySettings', href: '/settings' },
      { labelKey: 'webHelpButton.linkBillingPlans', href: '/billing' },
    ],
  },
};

const DEFAULT_HELP: PageHelpConfig = {
  links: [
    { labelKey: 'webHelpButton.linkViewDashboard', href: '/' },
    { labelKey: 'webHelpButton.linkBrowseHelpCenter', href: '/help' },
  ],
};

function getPageHelp(pathname: string): PageHelpConfig {
  if (PAGE_HELP[pathname]) return PAGE_HELP[pathname];

  // Match dynamic segments like /matches/:id
  if (pathname.startsWith('/matches/')) {
    return {
      links: [
        { labelKey: 'webHelpButton.linkHowRecordGoals', href: '#' },
        { labelKey: 'webHelpButton.linkHowCompleteMatch', href: '#' },
      ],
    };
  }
  if (pathname.startsWith('/players/')) {
    return {
      links: [
        { labelKey: 'webHelpButton.linkCoppaConsentGuide', href: '#' },
        { labelKey: 'webHelpButton.linkUnderstandingProfiles', href: '#' },
      ],
    };
  }
  if (pathname.startsWith('/teams/')) {
    return {
      links: [
        { labelKey: 'webHelpButton.linkManagingRosters', href: '#' },
        { labelKey: 'webHelpButton.linkTeamSettingsExplained', href: '#' },
      ],
    };
  }

  return DEFAULT_HELP;
}

export const HelpButton: React.FC = () => {
  React.useEffect(() => {
    injectKeyframes();
  }, []);
  const { t } = useTranslationTyped();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const pageHelp = getPageHelp(location.pathname);

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearchQuery('');
    buttonRef.current?.focus();
  }, []);

  // Close panel on Escape and trap focus inside
  useEffect(() => {
    if (!open) return;

    // Autofocus search input when panel opens
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleClose]);

  // Close panel when clicking outside
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, handleClose]);

  const fabStyle: CSSProperties = {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${theme.colors.accent.primary} 0%, ${theme.colors.accent.dark} 100%)`,
    boxShadow: `${theme.shadows.lg}, ${theme.shadows.glow}`,
    border: 'none',
    color: '#ffffff',
    fontSize: '22px',
    fontWeight: theme.typography.fontWeight.bold,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: theme.zIndex.fixed,
    transition: theme.transitions.base,
    fontFamily: theme.typography.fontFamily.primary,
  };

  const panelStyle: CSSProperties = {
    position: 'fixed',
    bottom: '96px',
    right: '24px',
    width: '320px',
    maxHeight: '480px',
    overflowY: 'auto',
    backgroundColor: theme.colors.bg.secondary,
    border: `1px solid ${theme.colors.border.primary}`,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadows.xl,
    zIndex: theme.zIndex.fixed,
    display: 'flex',
    flexDirection: 'column',
    animation: 'fadeIn 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const panelHeaderStyle: CSSProperties = {
    padding: `${theme.spacing.lg} ${theme.spacing.xl}`,
    borderBottom: `1px solid ${theme.colors.border.secondary}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  };

  const searchStyle: CSSProperties = {
    width: '100%',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: theme.colors.bg.tertiary,
    border: `1.5px solid ${theme.colors.border.secondary}`,
    borderRadius: theme.radius.md,
    color: theme.colors.text.primary,
    fontSize: theme.typography.fontSize.sm.size,
    fontFamily: theme.typography.fontFamily.primary,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const sectionTitleStyle: CSSProperties = {
    fontSize: theme.typography.fontSize.xs.size,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.text.tertiary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing.sm,
  };

  const linkItemStyle: CSSProperties = {
    display: 'block',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    color: theme.colors.accent.primary,
    fontSize: theme.typography.fontSize.sm.size,
    textDecoration: 'none',
    borderRadius: theme.radius.sm,
    cursor: 'pointer',
    transition: theme.transitions.fast,
    border: 'none',
    background: 'none',
    textAlign: 'left',
    width: '100%',
    fontFamily: theme.typography.fontFamily.primary,
  };

  const filteredLinks = searchQuery.trim()
    ? pageHelp.links.filter((l) =>
        t(l.labelKey as Parameters<typeof t>[0])
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
      )
    : pageHelp.links;

  return (
    <>
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-label={t('webHelpButton.panelAriaLabel')}
          style={panelStyle}
        >
          {/* Header */}
          <div style={panelHeaderStyle}>
            <span
              style={{
                fontSize: theme.typography.fontSize.base.size,
                fontWeight: theme.typography.fontWeight.semibold,
                color: theme.colors.text.primary,
              }}
            >
              {t('webHelpButton.panelTitle')}
            </span>
            <button
              onClick={handleClose}
              style={{
                background: 'none',
                border: 'none',
                color: theme.colors.text.secondary,
                fontSize: '20px',
                cursor: 'pointer',
                padding: theme.spacing.xs,
                display: 'flex',
                alignItems: 'center',
              }}
              aria-label={t('webHelpButton.closeAriaLabel')}
            >
              ✕
            </button>
          </div>

          {/* Search */}
          <div
            style={{
              padding: theme.spacing.lg,
              borderBottom: `1px solid ${theme.colors.border.secondary}`,
              flexShrink: 0,
            }}
          >
            <input
              ref={searchInputRef}
              type="search"
              placeholder={t('webHelpButton.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={searchStyle}
              aria-label={t('webHelpButton.searchAriaLabel')}
            />
          </div>

          {/* Content */}
          <div style={{ padding: theme.spacing.lg, flex: 1, overflowY: 'auto' }}>
            {/* Context-aware quick links */}
            <div style={{ marginBottom: theme.spacing.xl }}>
              <p style={sectionTitleStyle}>{t('webHelpButton.sectionQuickLinks')}</p>
              {filteredLinks.length === 0 ? (
                <p
                  style={{
                    fontSize: theme.typography.fontSize.sm.size,
                    color: theme.colors.text.muted,
                  }}
                >
                  {t('webHelpButton.noResults', { query: searchQuery })}
                </p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {filteredLinks.map((link) => (
                    <li key={link.labelKey}>
                      {link.action ? (
                        <button
                          style={linkItemStyle}
                          onClick={() => {
                            link.action?.();
                            handleClose();
                          }}
                        >
                          {t(link.labelKey as Parameters<typeof t>[0])}
                        </button>
                      ) : (
                        <a
                          href={link.href ?? '#'}
                          style={linkItemStyle}
                          target={link.href && link.href !== '#' ? '_blank' : undefined}
                          rel="noopener noreferrer"
                        >
                          {t(link.labelKey as Parameters<typeof t>[0])}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Footer links */}
            <div
              style={{
                borderTop: `1px solid ${theme.colors.border.secondary}`,
                paddingTop: theme.spacing.lg,
              }}
            >
              <p style={sectionTitleStyle}>{t('webHelpButton.sectionResources')}</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li>
                  <a
                    href={`mailto:${KANTERA_EMAIL.support}`}
                    style={linkItemStyle}
                    aria-label={t('webHelpButton.contactSupportAriaLabel')}
                  >
                    {t('webHelpButton.contactSupport')}
                  </a>
                </li>
                <li>
                  {/* KAN-208: this opened a docs site on the .io, a domain Kantera does not
                      hold (it does not resolve today; whoever registers it could serve a
                      Kantera-looking page to every signed-in user). The guide is the app's own
                      help centre. */}
                  <Link to="/help-center" style={linkItemStyle} onClick={handleClose}>
                    {t('webHelpButton.viewFullGuide')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <button
        ref={buttonRef}
        style={fabStyle}
        onClick={() => setOpen((prev) => !prev)}
        aria-label={open ? t('webHelpButton.closeAriaLabel') : t('webHelpButton.openAriaLabel')}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={t('webHelpButton.fabTitle')}
      >
        ?
      </button>
    </>
  );
};
